import React, { useState, useEffect } from "react";
import "./App.css";
import { motion, useScroll, useTransform } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay }
  })
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

export default function App() {
  const [dark, setDark] = useState(true);
  const [activeProject, setActiveProject] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  useEffect(() => {
    document.body.style.overflow = activeProject ? "hidden" : "auto";
  }, [activeProject]);

  const projects = [
    {
      id: 1,
      title: "AI-Driven TRL Assessment for Advanced Nuclear Systems",
      category: "Current Research",
      tags: ["Python", "LangChain", "LLMs", "Multi-Agent", "NLP", "BeautifulSoup", "Selenium"],
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=500&fit=crop",
      shortDesc: "Graduate research building AI-powered framework to assess Technology Readiness Levels of MARVEL microreactor using multi-agent architecture.",
      fullDesc: `Designing an intelligent system that automates technology maturity assessment for advanced nuclear microreactor systems using AI and NLP.

🎯 Challenge: Manually analyzing 100+ technical documents from DOE, national labs, and conferences to assess subsystem readiness.

💡 Solution: Multi-agent AI system with specialized agents for:
• Document Collection: BeautifulSoup & Selenium for automated scraping
• Evidence Extraction: NLP entity recognition for milestones and test results
• TRL Mapping: Semantic analysis to classify technology maturity (TRL 1-9)
• Gap Analysis: Identifies missing evidence and weaknesses

🔬 Technical Stack: Python, LangChain, LLMs, PyPDF2, spaCy, semantic similarity models

📊 Impact: Reduces manual assessment time from weeks to hours while maintaining expert-level accuracy across six critical subsystems (reactor core, heat transport, control systems, etc.).`,
      achievements: [
        "Automated analysis of 100+ technical documents",
        "Multi-agent architecture with specialized roles",
        "Real-time TRL assessment across 6 subsystems"
      ]
    },
    {
      id: 2,
      title: "Agentic AI for Medical Affairs Intelligence",
      category: "Graduate Capstone - Flagship",
      tags: ["Python", "NLP", "LLMs", "Agentic AI", "Streamlit", "spaCy", "GenAI"],
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop",
      shortDesc: "Multi-agent system processing 129 pharmaceutical articles to generate 432 healthcare professional insights with 90% precision.",
      fullDesc: `Architected end-to-end agentic AI pipeline transforming pharmaceutical intelligence into actionable Medical Affairs insights.

🏥 Problem: Medical Affairs teams manually track 50+ pharma newsrooms, clinical trials, and conference proceedings—spending 60+ hours/month staying current.

🤖 5-Agent Pipeline:
1. Scraper Agent: Automated collection from FDA, ASCO, ESMO, pharma sites
2. Entity Extractor: NLP-powered extraction of trials, drugs, indications using spaCy and LLMs
3. Categorizer: 75% accuracy across 8 medical categories (Clinical Trials, FDA Approvals, Research, etc.)
4. Question Generator: Created HCP-targeted poll questions with semantic grounding (0.628 avg score)
5. Publisher Agent: Automated social media distribution with quality validation

📊 Results:
• Processed 129 breast cancer articles over 3-month period
• Generated 432 grounded poll questions (3.3 per article)
• Achieved 90% precision through three-tier quality filtering
• Reduced manual monitoring by 60+ hours/month

🎯 Innovation: Auto-publish (≥0.75), Human Review (0.70-0.74), Reject (<0.70). Modular architecture enables multi-therapeutic expansion.`,
      github: "https://github.com/lasyapriyavavilala/breast-cancer-pipeline",
      achievements: [
        "90% precision with automated validation",
        "432 insights from 129 articles across 50+ sources",
        "Three-tier grounding system for MLR compliance",
        "60+ hours/month time savings for Medical Affairs"
      ]
    },
    {
      id: 3,
      title: "Hybrid CNN-LSTM for Predictive Maintenance",
      category: "Deep Learning",
      tags: ["CNN-LSTM", "TensorFlow", "Signal Processing", "Anomaly Detection", "t-SNE"],
      image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&h=500&fit=crop",
      shortDesc: "Deep learning model achieving 90% precision in machine failure prediction with 30% potential downtime reduction.",
      fullDesc: `Developed hybrid CNN-LSTM architecture for bearing failure prediction using NASA IMS Dataset, combining spatial and temporal feature extraction.

⚙️ Technical Approach:
• Multi-scale feature engineering: FFT, Wavelets, RMS, kurtosis, peak-to-peak
• CNN layers: Spatial pattern recognition from vibration signals
• LSTM layers: Temporal dependency modeling for failure progression
• Advanced preprocessing: Noise reduction, signal normalization
• t-SNE visualization: Unsupervised anomaly pattern discovery

🎯 Performance:
• 90% precision in early failure prediction
• 30+ hours advance warning before catastrophic failure
• Successfully detected inner race, outer race, and roller element failures
• Validated on industrial test-to-failure experiments (2000 RPM, 6000 lbs load)

💼 Business Impact:
30% potential reduction in unplanned downtime through early warning system. Cloud-based monitoring pipeline ready for industrial deployment across manufacturing facilities.`,
      github: "https://github.com/Sruthi96200/NASA-Bearing-Dataset",
      achievements: [
        "90% precision on bearing failure detection",
        "30% potential downtime reduction",
        "30+ hours advance failure warning"
      ]
    },
    {
      id: 4,
      title: "GPU-Accelerated Vehicle Routing Optimization",
      category: "Operations Research",
      tags: ["Julia", "Column Generation", "GPU", "Optimization", "Metaheuristics"],
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=500&fit=crop",
      shortDesc: "Julia-based optimization framework reducing routing computation time by 15% with GPU acceleration.",
      fullDesc: `Designed high-performance vehicle routing system using Column Generation with GPU parallelization for real-time fleet dispatching.

🚛 Problem Scope: Vehicle Routing Problem (VRP) with time windows, capacity constraints, driver hours regulations, and multi-depot operations.

⚡ Technical Innovation:
• Column Generation: Decomposed large-scale problems into tractable subproblems
• GPU Acceleration: Julia CUDA integration for parallel route evaluation
• Hybrid Approach: Combined exact methods with local search heuristics
• Constraint Handling: Time windows, capacity limits, operational variables
• Stochastic Benchmarking: Validated against probabilistic demand scenarios

🎯 Results:
• 15% computation time reduction vs CPU-only baseline
• Scaled to 500+ customer locations with sub-second response times
• 12% route efficiency improvement through better constraint exploitation
• Real-time replanning capability for dynamic route adjustments

💡 Impact: Enables logistics companies to optimize daily operations, reducing fuel costs and improving customer service through faster, more efficient routing.`,
      achievements: [
        "15% faster computation with GPU acceleration",
        "Scaled to 500+ locations in real-time",
        "12% route efficiency improvement"
      ]
    },
    {
      id: 5,
      title: "ACME Strategic Optimization Dashboard",
      category: "Business Analytics",
      tags: ["Linear Programming", "PuLP", "Streamlit", "Plotly", "Optimization"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop",
      shortDesc: "Data-driven optimization engine solving sales and margin allocation challenges across enterprise hierarchies.",
      fullDesc: `Built interactive optimization engine using linear programming to balance sales growth and profitability across corporate structures.

💼 Business Context: Enterprise strategy teams need data-driven guidance for resource allocation across Portfolio → Geography → Brand → Segment hierarchies with realistic financial constraints.

⚙️ Optimization Engine:
• 4 Strategic Scenarios using PuLP (Python Linear Programming):
  - Sales Maximization: Optimize revenue while meeting margin thresholds
  - Profit Maximization: Maximize gross margin with volume constraints
  - Balanced Target Approach: Multi-objective optimization
  - Feasibility Analysis: Automated detection of impossible targets
• Dynamic constraint modeling with hierarchical rollups
• 5-year projection forecasting with scenario comparison

📈 Interactive Dashboard (Streamlit):
• Pre/post-optimization performance visualization (Plotly)
• Real-time infeasibility detection with constraint violation highlighting
• Sensitivity analysis across growth parameters
• Export-ready reports for executive decision-making

🎯 Impact: Quantified trade-offs between growth and profitability, enabling strategic planning teams to set realistic, data-backed targets.`,
      github: "https://github.com/Sruthi96200/ProMazo_Assessment",
      achievements: [
        "4 optimization scenarios with constraint validation",
        "5-year forecasting with hierarchical rollups",
        "Interactive dashboard with Plotly visualizations"
      ]
    },
    {
      id: 6,
      title: "Overparameterization in Deep Learning (EuroSAT)",
      category: "Computer Vision",
      tags: ["CNN", "TensorFlow", "Model Compression", "Pruning", "Quantization"],
      image: "https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=800&h=500&fit=crop",
      shortDesc: "Benchmarked CNN architectures on satellite imagery, applying pruning and quantization for edge deployment.",
      fullDesc: `Explored overparameterization in CNNs for satellite image classification using EuroSAT dataset, investigating performance-capacity trade-offs.

🛰️ Research Focus: How does model capacity affect generalization, redundancy, and deployment feasibility?

🏗️ Architecture Design:
• SmallNet: Lightweight 3-layer CNN for edge devices
• MediumNet: Balanced architecture with dropout regularization
• LargeNet: Overparameterized network studying implicit regularization
• Optimizer Comparison: Benchmarked SGD, RMSprop, and Adam across capacities

🔬 Model Compression:
• L1-Norm Pruning: Removed redundant filters while maintaining accuracy
• Quantization: Reduced model size for efficient edge deployment
• Capacity Analysis: Studied parameter count vs generalization relationship
• Robustness Testing: Evaluated compressed models on validation set

📊 Key Findings:
• MediumNet achieved optimal accuracy-size trade-off
• Pruning removed 40% of parameters with <2% accuracy loss
• Quantization reduced model size by 75% for edge deployment
• Demonstrated implicit regularization benefits in overparameterized networks

🌍 Applications: Optimized models suitable for real-time satellite classification on resource-constrained edge devices for environmental monitoring.`,
      github: "https://github.com/Sruthi96200/EuroSat-Dataset",
      achievements: [
        "3 architecture variants with optimizer benchmarking",
        "40% parameter reduction via L1-norm pruning",
        "75% model size reduction through quantization"
      ]
    },
    {
      id: 7,
      title: "Database Management for Sentiment Analysis",
      category: "Data Engineering",
      tags: ["SQL Server", "T-SQL", "Database Design", "NLP", "Social Media Analytics"],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop",
      shortDesc: "Enterprise-scale relational database infrastructure for large-scale social media sentiment analysis.",
      fullDesc: `Developed comprehensive database management solution for storing and analyzing user-generated content from social media platforms at scale.

📊 Database Architecture:
• Star Schema Design: Optimized for OLAP-style queries and analytical processing
• Dimension Tables: Users, Posts, Platforms, Hashtags, Keywords, Sentiment_Scores, Languages, Locations
• Fact Tables: Post_Analytics (engagement metrics), Sentiment_Trends (time-series)
• ERD Modeling: Ensured data normalization and referential integrity across 10+ tables

🛠️ Technical Implementation:
• SQL Scripts: Automated table creation, population, and management workflows
• Complex Views: JOIN operations and WHERE clauses for filtered multi-dimensional analysis
• Audit Mechanism: Trigger-based change tracking with timestamps for compliance
• Stored Procedures: Advanced SQL logic for sentiment aggregation and trend detection
• UDFs: Custom functions for text preprocessing and sentiment scoring
• Cursor Operations: Row-wise processing for batch sentiment updates

📈 Use Cases:
• Time-series sentiment trend analysis across geographies and demographics
• Platform-specific engagement metrics and comparative analytics
• NLP pipeline integration with structured, preprocessed datasets
• Real-time dashboard support for BI and reporting tools

🎯 Impact: Enabled scalable sentiment monitoring for brand perception, public opinion tracking, and user engagement analysis across Twitter, Instagram, and Reddit.`,
      achievements: [
        "Star schema with 10+ normalized tables",
        "Trigger-based audit trail for compliance",
        "Stored procedures and UDFs for advanced analytics"
      ]
    },
    {
      id: 8,
      title: "Adult Income Dataset Analysis",
      category: "Data Science",
      tags: ["Python", "Pandas", "Seaborn", "Plotly", "EDA", "Statistical Analysis"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop",
      shortDesc: "Comprehensive socio-economic analysis of income disparities using statistical methods and visualization.",
      fullDesc: `Conducted in-depth exploratory data analysis on UCI Adult Income Dataset to investigate demographic, educational, and occupational factors influencing income levels.

📊 Dataset: 48,842 records with 14 demographic and socio-economic attributes
Target: Income >$50K vs ≤$50K classification

🔍 Analysis Methodology:
• Data Preprocessing: Handled missing values, outlier detection, normalization
• Univariate Analysis: Distribution profiling for all numerical and categorical variables
• Bivariate Analysis: Correlation studies and income relationship mapping
• Multivariate Analysis: Feature interactions and combined effects on income
• Feature Engineering: Derived meaningful groupings and categorical encodings

📈 Visualization Suite:
• Static Plots: Seaborn/Matplotlib for publication-quality graphics
• Interactive Dashboards: Plotly visualizations for dynamic exploration
• Heatmaps: Correlation matrices and feature importance rankings
• Categorical Plots: Income distribution across demographics

🎯 Key Findings:
• Education level: Strongest predictor of high income (>80% correlation)
• Gender and race disparities: Significant income gaps persist
• White-collar occupations + advanced degrees: Strongly associated with >$50K income
• Hours worked per week: Non-linear relationship with income levels

💡 Impact: Insights inform workforce development policies, equity assessments, and public policy formulation around income inequality and socio-economic mobility.`,
      achievements: [
        "48,842 records with 14-feature comprehensive analysis",
        "Multi-dimensional EDA with Seaborn/Plotly visualizations",
        "Policy-relevant insights on income inequality patterns"
      ]
    }
  ];

  const skills = [
    { 
      category: "Programming & Tools", 
      items: ["Python", "SQL", "R", "Bash", "Git", "Julia"] 
    },
    { 
      category: "Machine Learning", 
      items: ["TensorFlow", "PyTorch", "Scikit-learn", "XGBoost", "LightGBM", "Keras"] 
    },
    { 
      category: "NLP & Generative AI", 
      items: ["spaCy", "NLTK", "Transformers", "Hugging Face", "LangChain", "LLMs", "BERT", "GPT", "RAG", "Prompt Engineering"] 
    },
    { 
      category: "Agentic AI (Specialization)", 
      items: ["Multi-Agent Systems", "Agentic Workflows", "LangChain", "Agent Orchestration", "Tool Integration"] 
    },
    { 
      category: "Data Engineering & Processing", 
      items: ["PySpark", "Pandas", "NumPy", "Apache Spark", "Airflow", "ETL Pipelines"] 
    },
    { 
      category: "Visualization", 
      items: ["Tableau", "Power BI", "Matplotlib", "Seaborn", "Plotly", "Streamlit"] 
    },
    { 
      category: "Cloud & Deployment", 
      items: ["AWS", "Docker", "Kubernetes", "MLflow", "Azure", "Model Deployment"] 
    },
    { 
      category: "Databases", 
      items: ["PostgreSQL", "MySQL", "MongoDB", "SQL Server", "Database Design"] 
    },
    { 
      category: "Web Scraping & Automation", 
      items: ["BeautifulSoup", "Selenium", "Scrapy", "API Development", "Web Automation"] 
    },
    { 
      category: "Specialized Techniques", 
      items: ["Computer Vision", "Time Series Analysis", "Anomaly Detection", "A/B Testing", "Feature Engineering", "Model Interpretability"] 
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:sruthikeerthana960@gmail.com?subject=Portfolio Contact from ${formData.name}&body=${formData.message}%0D%0A%0D%0AFrom: ${formData.email}`;
    window.location.href = mailtoLink;
    setFormData({ name: "", email: "", message: "" });
  };

  return (
    <div className={dark ? "app dark" : "app light"}>
      <div className="bg-gradient"></div>
      <div className="bg-grid"></div>

      <motion.button className="toggle" onClick={() => setDark(!dark)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        {dark ? "☀️" : "🌙"}
      </motion.button>

      {/* Hero with Professional Photo */}
      <section className="hero">
        <motion.div style={{ opacity }} className="hero-content">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}>
            <motion.div className="profile-section">
              <motion.img 
                src={`${process.env.PUBLIC_URL}/profile.jpg`}
                alt="Sruthi Keerthana Nuttakki"
                className="profile-photo"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              />
            </motion.div>

            <motion.h1 className="name">Sruthi Keerthana Nuttakki</motion.h1>
            <motion.div className="accent-line"></motion.div>
            <motion.p className="tagline">Machine Learning Engineer | Graduate Research Assistant</motion.p>
            <motion.p className="subtitle">
              Building intelligent systems that solve complex problems across industries
              <br />
              <span className="highlight">Agentic AI</span> • <span className="highlight">Multi-Agent Systems</span> • <span className="highlight">NLP</span> • <span className="highlight">Deep Learning</span>
            </motion.p>
            <motion.div className="education">🎓 MS Data Science @ ASU | Dec 2025</motion.div>
            <motion.div className="status-badge">✨ Currently: Graduate RA @ ASU | Seeking Full-Time ML/AI Roles</motion.div>
            <motion.div className="hero-links">
              <a href={`${process.env.PUBLIC_URL}/Sruthi_Nuttakki_Resume.pdf`} download className="cta-btn">📄 Resume</a>
              <a href="https://github.com/Sruthi96200" target="_blank" rel="noreferrer" className="cta-btn secondary">💻 GitHub</a>
              <a href="https://www.linkedin.com/in/sruthi-keerthana-nuttakki" target="_blank" rel="noreferrer" className="cta-btn secondary">🔗 LinkedIn</a>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* About with Casual Photo */}
      <motion.section className="section about" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <div className="container">
          <h2 className="section-title"><span className="emoji">🔄</span> Data to Decisions</h2>
          <div className="about-wrapper">
            <motion.img 
              src={`${process.env.PUBLIC_URL}/profile-casual.jpg`}
              alt="Sruthi - Casual"
              className="about-photo"
              variants={fadeUp}
            />
            <div className="about-content">
              <p>
                This is how I approach every problem: I transform data chaos into systems that actually work. Predictive models that catch failures before they cascade. Intelligent pipelines that automate what teams spend weeks doing manually. AI that doesn't just process information but creates value people can measure.
              </p>
              <p>
                Graduate Research Assistant at ASU working on AI frameworks for advanced technology assessment. Recent MS Data Science grad who's spent real time in production environments, not just notebooks. I gravitate toward complex problems where multi-agent systems, NLP, and scalable architecture intersect.
              </p>
              <p>
                <strong>What sets me apart:</strong> I translate between data scientists and executives. I can build the system AND articulate its ROI. Technical depth meets business fluency. Code that ships, not just experiments that impress in demos.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Projects - ALL 8 */}
      <section className="section projects-section">
        <div className="container">
          <motion.h2 className="section-title" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="emoji">🚀</span> Featured Projects
          </motion.h2>
          <motion.div className="projects-grid" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {projects.map((project) => (
              <motion.div
                key={project.id}
                className={`project-card ${project.id === 2 ? "flagship" : ""}`}
                variants={fadeUp}
                whileHover={{ y: -5 }}
                onClick={() => setActiveProject(project)}
              >
                {project.id === 2 && <div className="flagship-badge">⭐ Flagship</div>}
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay"><span>View Details →</span></div>
                </div>
                <div className="project-content">
                  <div className="project-category">{project.category}</div>
                  <h3>{project.title}</h3>
                  <p>{project.shortDesc}</p>
                  <div className="project-tags">
                    {project.tags.map((tag) => (<span key={tag} className="tag">{tag}</span>))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Skills - COMPREHENSIVE */}
      <motion.section className="section skills-section" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <div className="container">
          <h2 className="section-title"><span className="emoji">🛠</span> Technical Expertise</h2>
          <motion.div className="skills-grid" variants={stagger}>
            {skills.map((skillGroup) => (
              <motion.div key={skillGroup.category} className="skill-group" variants={fadeUp}>
                <h3 className="skill-category">{skillGroup.category}</h3>
                <div className="skill-items">
                  {skillGroup.items.map((skill) => (<span key={skill} className="skill-badge">{skill}</span>))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Experience */}
      <motion.section className="section experience-section" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <div className="container">
          <h2 className="section-title"><span className="emoji">🏢</span> Professional Journey</h2>
          <div className="timeline">
            <motion.div className="timeline-item" variants={fadeUp}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3>Graduate Research Assistant</h3>
                <div className="timeline-meta">Arizona State University • Feb 2026 – Present</div>
                <ul>
                  <li>Designing AI-driven TRL assessment framework for MARVEL nuclear microreactor using multi-agent architecture</li>
                  <li>Building document ingestion pipelines to analyze 100+ technical documents from DOE and national labs</li>
                  <li>Applying NLP entity extraction and semantic similarity analysis across six microreactor subsystems</li>
                </ul>
              </div>
            </motion.div>
            <motion.div className="timeline-item" variants={fadeUp}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3>Machine Learning Engineer</h3>
                <div className="timeline-meta">INSOFE, UpGrad • Oct 2021 – Apr 2023</div>
                <div className="timeline-meta" style={{fontSize: "0.75rem", fontStyle: "italic", marginTop: "0.25rem"}}>
                  Career Progression: ML Trainee → Consulting Intern → Full-Time Engineer
                </div>
                <ul>
                  <li>Built production NLP platform processing 10,000+ documents with text classification, NER, and sentiment analysis</li>
                  <li>Led customer segmentation for Dallas-based client analyzing 50,000+ records, driving 20% ROI improvement</li>
                  <li>Developed SQL-backed Tableau dashboards visualizing 15+ KPIs, reducing manual analysis by 40 hours/month</li>
                </ul>
              </div>
            </motion.div>
            <motion.div className="timeline-item" variants={fadeUp}>
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h3>Admissions Supervisor</h3>
                <div className="timeline-meta">Arizona State University • June 2024 – Dec 2025 (Part-time)</div>
                <ul>
                  <li>Led team of 8 processing 5,000+ applications, improving data accuracy 20% through automated Python validation scripts</li>
                  <li>Designed quality control workflows reducing processing errors and enhancing applicant experience</li>
                  <li>Mentored staff on compliance protocols and data management best practices</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Certifications */}
      <motion.section className="section certs-section" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <div className="container">
          <h2 className="section-title"><span className="emoji">🎓</span> Certifications</h2>
          <motion.div className="certs-grid" variants={stagger}>
            <motion.div className="cert-card" variants={fadeUp}>
              <div className="cert-icon">🎯</div>
              <h3>Computational Data Science</h3>
              <p>UpGrad INSOFE & Case Western, 2022</p>
            </motion.div>
            <motion.div className="cert-card" variants={fadeUp}>
              <div className="cert-icon">🤖</div>
              <h3>Machine Learning I</h3>
              <p>Columbia University, 2025</p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact */}
      <motion.section className="section contact-section" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
        <div className="container">
          <h2 className="section-title"><span className="emoji">📬</span> Let's Build Something</h2>
          <div className="contact-wrapper">
            <motion.div className="contact-info" variants={fadeUp}>
              <h3>Open to Opportunities</h3>
              <p>Seeking full-time ML Engineer or Data Scientist roles where intelligent systems meet real-world impact.</p>
              <div className="contact-links">
                <a href="mailto:sruthikeerthana960@gmail.com" className="contact-link">
                  <span className="icon">✉️</span>
                  <div><div className="link-label">Email</div><div className="link-value">sruthikeerthana960@gmail.com</div></div>
                </a>
                <a href="tel:+16027304639" className="contact-link">
                  <span className="icon">📱</span>
                  <div><div className="link-label">Phone</div><div className="link-value">(602) 730-4639</div></div>
                </a>
                <a href="https://www.linkedin.com/in/sruthi-keerthana-nuttakki" target="_blank" rel="noreferrer" className="contact-link">
                  <span className="icon">💼</span>
                  <div><div className="link-label">LinkedIn</div><div className="link-value">sruthi-keerthana-nuttakki</div></div>
                </a>
              </div>
            </motion.div>
            <motion.form className="contact-form" onSubmit={handleSubmit} variants={fadeUp}>
              <div className="form-group">
                <label htmlFor="name">Your Name</label>
                <input type="text" id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required placeholder="John Doe" />
              </div>
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input type="email" id="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required placeholder="john@example.com" />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea id="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows="4" placeholder="Tell me about the opportunity..."></textarea>
              </div>
              <motion.button type="submit" className="submit-btn" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>Send Message →</motion.button>
            </motion.form>
          </div>
        </div>
      </motion.section>

      <footer className="footer">
        <p>© 2025 Sruthi Keerthana Nuttakki • Building intelligent systems that matter</p>
      </footer>

      {/* Modal */}
      {activeProject && (
        <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={() => setActiveProject(null)}>
          <motion.div className="modal-content" initial={{ scale: 0.9 }} animate={{ scale: 1 }} onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveProject(null)}>✕</button>
            <div className="modal-header">
              <img src={activeProject.image} alt={activeProject.title} />
              {activeProject.id === 2 && <div className="flagship-badge-large">⭐ Flagship Project</div>}
            </div>
            <div className="modal-body">
              <div className="modal-category">{activeProject.category}</div>
              <h2>{activeProject.title}</h2>
              <div className="modal-tags">
                {activeProject.tags.map((tag) => (<span key={tag} className="tag">{tag}</span>))}
              </div>
              <div className="modal-description">
                {activeProject.fullDesc.split('\n').map((para, idx) => (para.trim() && <p key={idx}>{para}</p>))}
              </div>
              {activeProject.achievements && (
                <div className="achievements">
                  <h3>🏆 Key Achievements</h3>
                  <ul>{activeProject.achievements.map((achievement, idx) => (<li key={idx}>{achievement}</li>))}</ul>
                </div>
              )}
              {activeProject.github && (<a href={activeProject.github} target="_blank" rel="noreferrer" className="github-link"><span>💻</span> View on GitHub →</a>)}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}